import Vue from 'vue'
import 'vue-loading-overlay/dist/vue-loading.css'
import VueRouter from 'vue-router'
import store from './store'

/** Overlay only if a cold navigation is still waiting (in-app swaps stay instant). */
let navLoaderTimer = null;
let navLoader = null;

function clearNavLoader() {
    if (navLoaderTimer) {
        clearTimeout(navLoaderTimer);
        navLoaderTimer = null;
    }
    if (navLoader) {
        navLoader.hide();
        navLoader = null;
    }
}

function scheduleNavLoader() {
    clearNavLoader();
    navLoaderTimer = setTimeout(() => {
        navLoaderTimer = null;
        navLoader = Vue.$loading.show();
    }, 800);
}

function routeHasValidate(matched, key) {
    return matched.some((m) => m.meta.validate && m.meta.validate.indexOf(key) > -1);
}

function isInAppAuthNav(to, from) {
    return helper.isAuth()
        && from.matched.length > 0
        && routeHasValidate(to.matched, 'auth')
        && routeHasValidate(from.matched, 'auth');
}

function getAuthRedirect(to) {
    if (helper.getConfig('failed_install') && to.fullPath != '/install') {
        return { path: '/install' };
    }
    if (helper.getConfig('ip_filter') && localStorage.getItem('ip_restricted') && to.fullPath != '/ip-restricted') {
        return { path: '/ip-restricted' };
    }
    if (helper.isAuth() && !helper.hasRole('admin') && helper.getConfig('maintenance_mode') && to.fullPath != '/maintenance') {
        return { path: '/maintenance' };
    }

    const m = to.matched.find((item) => item.meta.validate);
    if (!m) {
        return null;
    }
    const validate = m.meta.validate;

    if (validate.indexOf('auth') > -1 && !helper.isAuth()) {
        toastr.error(i18n.auth.auth_required);
        return { path: '/login' };
    }
    if (validate.indexOf('two_factor') > -1 && helper.getConfig('two_factor_security') && helper.getTwoFactorCode()) {
        return { path: '/auth/security' };
    }
    if (validate.indexOf('lock_screen') > -1 && helper.getConfig('lock_screen') && helper.isScreenLocked()) {
        return { path: '/auth/lock' };
    }
    if (validate.indexOf('guest') > -1 && helper.isAuth()) {
        toastr.error(i18n.auth.guest_required);
        return { path: '/home' };
    }
    return null;
}

let routes = [
    {
        path: '/',                                       // all the routes which needs authentication + two factor authentication + lock screen
        component: require('./layouts/default-page').default,
        meta: { validate: ['auth','two_factor','lock_screen'] },
        children: [
            {
                path: '/',
                component: require('./views/pages/home').default
            },
            {
                path: '/home',
                component: require('./views/pages/home').default
            },
            {
                path: '/about',
                component: require('./views/pages/about').default
            },
            {
                path: '/update',
                component: require('./views/pages/update').default
            },
            {
                path: '/support',
                component: require('./views/pages/support').default
            },
            {
                path: '/profile',
                component: require('./views/pages/profile').default
            },
            {
                path: '/change-password',
                component: require('./views/pages/change-password').default
            },
            {
                path: '/blank',
                component: require('./views/pages/blank').default
            },
            {
                path: '/configuration',
                component: require('./views/configuration/basic/index').default
            },
            {
                path: '/configuration/logo',
                component: require('./views/configuration/logo/index').default
            },
            {
                path: '/configuration/mail',
                component: require('./views/configuration/mail/index').default
            },
            {
                path: '/backup',
                component: require('./views/backup/index').default
            },
            {
                path: '/configuration/basic',
                component: require('./views/configuration/basic/index').default
            },
            {
                path: '/configuration/system',
                component: require('./views/configuration/system/index').default
            },
            {
                path: '/configuration/server',
                component: require('./views/configuration/server/index').default
            },
            {
                path: '/configuration/role',
                component: require('./views/configuration/role/index').default
            },
            {
                path: '/configuration/authentication',
                component: require('./views/configuration/authentication/index').default
            },
            // {
            //     path: '/configuration/sms',
            //     component: require('./views/configuration/sms/index').default
            // },
            {
                path: '/configuration/instance',
                component: require('./views/configuration/instance/index').default
            },
            {
                path: '/configuration/scheduled-job',
                component: require('./views/configuration/scheduled-job/index').default
            },
            {
                path: '/configuration/ip-filter',
                component: require('./views/configuration/ip-filter/index').default
            },
            {
                path: '/configuration/ip-filter/:id/edit',
                component: require('./views/configuration/ip-filter/edit').default
            },
            {
                path: '/configuration/permission',
                component: require('./views/configuration/permission/index').default
            },
            {
                path: '/configuration/permission/assign',
                component: require('./views/configuration/permission/assign').default
            },
            {
                path: '/configuration/locale',
                component: require('./views/configuration/locale/index').default
            },
            {
                path: '/configuration/locale/:id/edit',
                component: require('./views/configuration/locale/edit').default
            },
            {
                path: '/configuration/locale/:locale',
                component: require('./views/configuration/locale/view').default
            },
            {
                path: '/configuration/locale/:locale/:module',
                component: require('./views/configuration/locale/view').default
            },
            {
                path: '/email-template',
                component: require('./views/email-template/index').default
            },
            {
                path: '/email-template/:id/edit',
                component: require('./views/email-template/edit').default
            },
            {
                path: '/email-log',
                component: require('./views/email-log/index').default
            },
            {
                path: '/activity-log',
                component: require('./views/activity-log/index').default
            },
            {
                path: '/todo',
                component: require('./views/todo/index').default
            },
            {
                path: '/todo/:id/edit',
                component: require('./views/todo/edit').default
            },
            {
                path: '/user',
                component: require('./views/user/index').default
            },
            {
                path: '/user/:id',
                component: require('./views/user/basic').default
            },
            {
                path: '/user/:id/basic',
                component: require('./views/user/basic').default
            },
            {
                path: '/user/:id/contact',
                component: require('./views/user/contact').default
            },
            {
                path: '/user/:id/avatar',
                component: require('./views/user/avatar').default
            },
            {
                path: '/user/:id/social',
                component: require('./views/user/social').default
            },
            {
                path: '/user/:id/password',
                component: require('./views/user/password').default
            },
            {
                path: '/user/:id/email',
                component: require('./views/user/email').default
            },
            {
                path: '/message',
                component: require('./views/message/index').default
            },
            {
                path: '/message/compose',
                component: require('./views/message/index').default
            },
            {
                path: '/message/inbox',
                component: require('./views/message/inbox').default
            },
            {
                path: '/message/sent',
                component: require('./views/message/sent').default
            },
            {
                path: '/message/important',
                component: require('./views/message/important').default
            },
            {
                path: '/message/trash',
                component: require('./views/message/trash').default
            },
            {
                path: '/message/draft',
                component: require('./views/message/draft').default
            },
            {
                path: '/message/:uuid/draft',
                component: require('./views/message/edit-draft').default
            },
            {
                path: '/message/:uuid',
                component: require('./views/message/view').default
            },
            {
                path: '/currency',
                component: require('./views/currency/index').default
            },
            {
                path: '/currency/:id/edit',
                component: require('./views/currency/edit').default
            },
            {
                path: '/plan',
                component: require('./views/plan/index').default
            },
            {
                path: '/plan/:id/edit',
                component: require('./views/plan/edit').default
            },
            {
                path: '/configuration/payment',
                component: require('./views/configuration/payment/index').default
            },
            {
                path: '/query',
                component: require('./views/query/index').default
            },
            {
                path: '/lead',
                component: require('./views/lead/index').default
            },
            {
                path: '/blog',
                component: require('./views/blog/index').default
            },
            {
                path: '/blog/:id/edit',
                component: require('./views/blog/edit').default
            },
            {
                path: '/subscriber',
                component: require('./views/subscriber/index').default
            },
            {
                path: '/configuration/frontend',
                component: require('./views/configuration/frontend/index').default
            },
            {
                path: '/instance',
                component: require('./views/instance/index').default
            },
            {
                path: '/instance/:id',
                component: require('./views/instance/view').default
            },
            {
                path: '/subscription',
                component: require('./views/subscription/index').default
            }
        ]
    },
    {
        path: '/',
        component: require('./layouts/guest-page').default,
        meta: { validate: ['auth'] },
        children: [
            {
                path: '/auth/security',
                component: require('./views/auth/security').default
            },
            {
                path: '/auth/lock',
                component: require('./views/auth/lock').default
            },
        ]
    },
    {
        path: '/',                      // all the routes which can be access without authentication
        component: require('./layouts/guest-page').default,
        meta: { validate: ['guest'] },
        children: [
            {
                path: '/',
                component: require('./views/auth/login').default
            },
            {
                path: '/login',
                component: require('./views/auth/login').default
            },
            {
                path: '/password',
                component: require('./views/auth/password').default
            },
            {
                path: '/password/reset/:token',
                component: require('./views/auth/reset').default
            },
            {
                path: '/install',
                component: require('./views/install/index').default
            }
        ]
    },
    {
        path: '/',
        component : require('./layouts/guest-page').default,
        children: [
            {
                path: '/terms-and-conditions',
                component: require('./views/pages/terms-and-conditions').default
            }
        ]
    },
    {
        path: '/',
        component : require('./layouts/error-page').default,
        children: [
            {
                path: '/terms-and-conditions',
                component: require('./views/pages/terms-and-conditions').default
            },
            {
                path: '/ip-restricted',
                component: require('./views/errors/ip-restricted').default
            },
            {
                path: '/maintenance',
                component: require('./views/errors/maintenance').default
            }
        ]
    },
    {
        path: '*',
        component : require('./layouts/error-page').default,
        children: [
            {
                path: '*',
                component: require('./views/errors/page-not-found').default
            }
        ]
    }
];

const routerBase =
    typeof window !== 'undefined' && window.__ADMIN_ROUTER_BASE__
        ? String(window.__ADMIN_ROUTER_BASE__).replace(/\/$/, '')
        : '/';

const router = new VueRouter({
    routes,
    base: routerBase,
    linkActiveClass: 'active',
    mode: 'history',
    scrollBehavior (to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition
        } else {
            return { x: 0, y: 0 }
        }
    }
});

router.beforeEach((to, from, next) => {
    if (isInAppAuthNav(to, from)) {
        next();
        helper.check()
            .then(() => {
                helper.notification();
                const dest = getAuthRedirect(to);
                if (dest) {
                    router.replace(dest);
                }
            })
            .catch(() => {
                store.dispatch('resetAuthUserDetail');
                router.replace({ path: '/login' });
            });
        return;
    }

    scheduleNavLoader();

    helper.check()
        .then(() => {
            helper.notification();
            const dest = getAuthRedirect(to);
            if (dest) {
                clearNavLoader();
                return next(dest);
            }
            return next();
        })
        .catch(() => {
            store.dispatch('resetAuthUserDetail');
            clearNavLoader();
            return next({ path: '/login' });
        });
});

router.afterEach(() => {
    clearNavLoader();
});

export default router;
